/**
 * registerDrawer — Componente drawer registrazione (3 step).
 *
 * Step 1: Nome, Cognome, Email, Password, CF
 * Step 2: Professione, Disciplina, N. iscrizione ordine, Situazione lavorativa
 * Step 3: Privacy, T&C, Newsletter
 *
 * Il CF viene parsificato lato client per estrarre sesso e data di nascita.
 * Uso nel template: <div x-data="registerDrawer">
 */
import { readProfession } from '../utils/profession-cookie.js';

export function registerRegisterDrawer(Alpine) {
    Alpine.data('registerDrawer', () => ({
        step: 1,

        // Step 1
        name: '',
        surname: '',
        email: '',
        password: '',
        showPassword: false,
        fiscalCode: '',
        passwordStrength: 0,   // 0-4
        cfError: '',
        dateOfBirth: '',       // YYYY-MM-DD, auto-derivata dal CF (modificabile)
        cityOfBirth: '',       // stringa visibile (es. "Roma")

        // Google Places — città di nascita
        birthCitySuggestions: [],
        birthCityLoading: false,
        birthCityOpen: false,
        _birthCityDebounce: null,
        _birthGLocation: null, // raw address_components per g_location_of_birth

        // Step 2 — dati caricati da API
        occupations: [],
        workSituations: [],
        occupationId: '',
        disciplines: [],
        disciplineId: '',
        orderName: '',
        orderNum: '',
        orderNumRequired: false,
        workSituationId: '',
        loadingOccupations: false,
        loadingDisciplines: false,

        // Step 3
        acceptPrivacy: false,
        acceptTerms: false,
        newsletterOptOut: false,   // casella "Non desidero ricevere...": spuntata = nessun consenso

        // Errori campo per campo (da validation_messages API)
        fieldErrors: {},

        // Conflitti email/CF già in uso (rilevati al nextStep dello step 1)
        accountConflict: null,   // null | 'email' | 'fiscal_code' | 'both'
        checkingAvailability: false,

        // OAuth
        oauthCompletion: false,   // true quando si arriva da "Continua con Google" (nuovo utente)
        oauthProviderId: '',      // Google provider_id (sub) da passare al register
        oauthLoading: false,

        async init() {
            this.$watch('$store.auth.registerDrawerOpen', (open) => {
                if (open && !this.oauthCompletion) this._onOpen();
            });

            // Ascolta evento di pre-compilazione dati OAuth
            window.addEventListener('oauth-prefill', (e) => {
                const d = e.detail || {};
                this._resetFields();
                this.oauthCompletion  = true;
                this.oauthProviderId  = d.provider_id || '';
                this.name    = d.name    || '';
                this.surname = d.surname || '';
                this.email   = d.email   || '';
                this.step    = 1;
                this.$store.auth.openRegisterDrawer();
            });
        },

        _onOpen() {
            this.step = 1;
            this._resetFields();
        },

        _resetFields() {
            this.name = '';
            this.surname = '';
            this.email = '';
            this.password = '';
            this.showPassword = false;
            this.fiscalCode = '';
            this.passwordStrength = 0;
            this.cfError = '';
            this.dateOfBirth = '';
            this.cityOfBirth = '';
            this.birthCitySuggestions = [];
            this.birthCityLoading = false;
            this.birthCityOpen = false;
            this._birthGLocation = null;
            this.occupationId = '';
            this.disciplines = [];
            this.disciplineId = '';
            this.orderName = '';
            this.orderNum = '';
            this.orderNumRequired = false;
            this.workSituationId = '';
            this.acceptPrivacy = false;
            this.acceptTerms = false;
            this.newsletterOptOut = false;
            this.fieldErrors = {};
            this.accountConflict = null;
            this.checkingAvailability = false;
            this.oauthCompletion = false;
            this.oauthProviderId = '';
            this.oauthLoading = false;
            this.$store.auth.error = '';
        },

        loginWithGoogle() {
            const w    = 520;
            const h    = 640;
            const left = Math.round(screen.width / 2 - w / 2);
            const top  = Math.round(screen.height / 2 - h / 2);

            this.oauthLoading = true;
            this.$store.auth.error = '';

            // Passa il cart_token guest al popup così google-start.php lo salva in sessione
            let startUrl = `${window.IKOS.oauthBase}/google-start.php`;
            const cartToken = localStorage.getItem('ik_cart_token');
            if (cartToken) {
                startUrl += '?cart_token=' + encodeURIComponent(cartToken);
            }

            const popup = window.open(
                startUrl,
                'oauth-google',
                `width=${w},height=${h},left=${left},top=${top},popup=1`
            );

            const pollClose = setInterval(() => {
                if (popup && popup.closed) {
                    clearInterval(pollClose);
                    this.oauthLoading = false;
                }
            }, 500);

            const onMessage = (e) => {
                if (e.origin !== window.location.origin) return;
                clearInterval(pollClose);
                window.removeEventListener('message', onMessage);
                this.oauthLoading = false;

                if (e.data?.type === 'oauth-success') {
                    // Il merge guest → utente è avvenuto, il cart_token guest non serve più
                    localStorage.removeItem('ik_cart_token');
                    const isInline = this.$store.auth.inlineMode
                        || sessionStorage.getItem('ik_checkout_pending') === '1';
                    if (isInline) {
                        sessionStorage.removeItem('ik_checkout_pending');
                        window.IKOS.authToken = e.data.authToken || true;
                        this.$store.auth.isLogged = true;
                        this.$store.auth.user = e.data.user ?? { name: '', surname: '' };
                        this.$store.auth._closeRegisterDrawerSilent();
                        Alpine.store('cart').refresh();
                        Alpine.store('checkout').openPreview();
                        Alpine.store('wishlist').refresh();
                    } else {
                        // Utente gia' registrato che entra con Google: e' un login, non una
                        // registrazione (con 'register' il tracking emetteva un sign_up falso)
                        sessionStorage.setItem('ik_login_method', 'oauth');
                        sessionStorage.setItem('ik_auth_just_logged', '1');
                        sessionStorage.setItem('ik_wishlist_just_logged', '1');
                        window.location.href = this.$store.auth.returnUrl || window.location.href;
                    }
                } else if (e.data?.type === 'oauth-needs-completion') {
                    this.$store.auth.closeRegisterDrawer();
                    this.$nextTick(() => {
                        window.dispatchEvent(new CustomEvent('oauth-prefill', {
                            detail: {
                                ...e.data.userData,
                                provider_id: e.data.provider_id || '',
                            },
                        }));
                    });
                } else if (e.data?.type === 'oauth-error') {
                    this.$store.auth.error = e.data.message || 'Errore accesso con Google.';
                }
            };

            window.addEventListener('message', onMessage);
        },

        // ── Password strength ────────────────────────────────────────
        onPasswordInput() {
            const p = this.password;
            let score = 0;
            if (p.length >= 8)  score++;
            if (p.length >= 12) score++;
            if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
            if (/[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p)) score++;
            this.passwordStrength = score;
        },

        get strengthLabel() {
            return ['', 'Debole', 'Discreta', 'Buona', 'Ottima'][this.passwordStrength] || '';
        },

        get strengthClass() {
            return ['', 'ik-register-drawer__strength--1', 'ik-register-drawer__strength--2',
                        'ik-register-drawer__strength--3', 'ik-register-drawer__strength--4'][this.passwordStrength] || '';
        },

        // ── Getter riepilogo step 3 ──────────────────────────────────
        get summaryOccupationName() {
            return this.occupations.find(o => String(o.id) === String(this.occupationId))?.name ?? '—';
        },

        get summaryDisciplineName() {
            return this.disciplines.find(d => String(d.id) === String(this.disciplineId))?.name ?? '—';
        },

        get summaryWorkSituationName() {
            return this.workSituations.find(w => String(w.id) === String(this.workSituationId))?.name ?? '—';
        },

        // ── Codice Fiscale ───────────────────────────────────────────
        onCfInput() {
            this.cfError = '';
            this.accountConflict = null;
            this.fiscalCode = this.fiscalCode.toUpperCase();
            const cf = this.fiscalCode.trim();
            if (cf.length === 16) {
                if (!this._validateCfFormat(cf)) {
                    this.cfError = 'Formato codice fiscale non valido.';
                } else {
                    // Auto-compila la data di nascita derivandola dal CF.
                    // Resta modificabile dall'utente (es. CF esteri o casi limite).
                    const dob = this._birthDateFromCf(cf);
                    if (dob) this.dateOfBirth = dob;
                }
            }
        },

        _validateCfFormat(cf) {
            return /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(cf);
        },

        /**
         * Estrae la data di nascita (YYYY-MM-DD) da un codice fiscale italiano valido.
         * Struttura CF: [6-7] anno, [8] mese (lettera), [9-10] giorno (>40 = femmina).
         * Stessa logica del backend (api/src/handlers/register.php). Restituisce '' se non decodificabile.
         */
        _birthDateFromCf(cf) {
            const monthMap = { A:1, B:2, C:3, D:4, E:5, H:6, L:7, M:8, P:9, R:10, S:11, T:12 };
            const month = monthMap[cf.charAt(8)];
            if (!month) return '';

            const yy  = parseInt(cf.substr(6, 2), 10);
            let   day = parseInt(cf.substr(9, 2), 10);
            if (Number.isNaN(yy) || Number.isNaN(day)) return '';

            if (day > 40) day -= 40;           // giorno > 40 = femmina
            if (day < 1 || day > 31) return '';

            // Anno a 4 cifre: soglia sull'anno corrente a 2 cifre
            const currentYY = new Date().getFullYear() % 100;
            const year = yy <= currentYY ? 2000 + yy : 1900 + yy;

            const mm = String(month).padStart(2, '0');
            const dd = String(day).padStart(2, '0');
            return `${year}-${mm}-${dd}`;
        },

        // ── Google Places — città di nascita ─────────────────────────
        fetchBirthCitySuggestions(val) {
            clearTimeout(this._birthCityDebounce);
            // Se l'utente modifica il testo, invalida la selezione precedente
            // (andrà riselezionata dall'elenco) e ripulisce l'eventuale errore.
            this._birthGLocation = null;
            delete this.fieldErrors.city_of_birth;
            if (val.length < 2) { this.birthCitySuggestions = []; this.birthCityOpen = false; return; }
            this._birthCityDebounce = setTimeout(async () => {
                this.birthCityLoading = true;
                try {
                    const res = await fetch(window.IKOS.apiBase + '/v1/place/autocomplete?input=' + encodeURIComponent(val));
                    const data = await res.json();
                    this.birthCitySuggestions = data.payload || [];
                    this.birthCityOpen = this.birthCitySuggestions.length > 0;
                } catch (e) {}
                this.birthCityLoading = false;
            }, 300);
        },

        async selectBirthCity(place) {
            this.birthCityOpen = false;
            this.birthCityLoading = true;
            try {
                const res = await fetch(window.IKOS.apiBase + '/v1/place/details?place_id=' + place.place_id);
                const data = await res.json();
                const components = data.payload?.address_components || [];
                let city = '';
                components.forEach(c => {
                    if (c.types.includes('locality'))                             city = c.short_name;
                    if (c.types.includes('administrative_area_level_3') && !city) city = c.short_name;
                });
                this.cityOfBirth = city || place.description || '';
                // Salva i raw components per g_location_of_birth (l'API ne ricava
                // città/provincia/stato di nascita tramite ApiUserValidator).
                this._birthGLocation = components;
            } catch (e) {}
            this.birthCityLoading = false;
        },

        // ── Navigazione step ─────────────────────────────────────────
        async nextStep() {
            this.fieldErrors = {};
            this.accountConflict = null;
            this.$store.auth.error = '';

            if (this.step === 1) {
                if (!this._validateStep1()) return;

                // Verifica disponibilità email e CF prima di procedere
                const available = await this._checkAvailability();
                if (!available) return;
            }

            if (this.step === 2 && !this._validateStep2()) return;

            this.step++;

            // Carica dati step 2 solo quando ci si arriva
            if (this.step === 2) {
                this._loadOccupations();
                this._loadWorkSituations();
            }
        },

        prevStep() {
            this.$store.auth.error = '';
            this.accountConflict = null;
            this.step--;
        },

        // ── Verifica disponibilità email / CF ────────────────────────
        async _checkAvailability() {
            this.checkingAvailability = true;
            try {
                const res = await fetch(`${window.IKOS.ajaxBase}/check-availability.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email:       this.email.trim(),
                        fiscal_code: this.fiscalCode.trim().toUpperCase(),
                    }),
                });
                const data = await res.json().catch(() => ({ available: true }));

                if (!data.available) {
                    const conflicts = data.conflicts || {};
                    if (conflicts.email && conflicts.fiscal_code) {
                        this.accountConflict = 'both';
                    } else if (conflicts.email) {
                        this.accountConflict = 'email';
                    } else {
                        this.accountConflict = 'fiscal_code';
                    }
                    return false;
                }
                return true;
            } catch {
                // In caso di errore di rete lasciamo passare (il backend rivalidrà al submit)
                return true;
            } finally {
                this.checkingAvailability = false;
            }
        },

        get accountConflictMessage() {
            if (this.accountConflict === 'email') {
                return 'Questa email è già associata a un account.';
            }
            if (this.accountConflict === 'fiscal_code') {
                return 'Questo codice fiscale è già associato a un account.';
            }
            if (this.accountConflict === 'both') {
                return 'Questa email e questo codice fiscale sono già associati a un account esistente.';
            }
            return '';
        },

        _validateStep1() {
            let ok = true;
            if (!this.name.trim())    { this.fieldErrors.name    = 'Campo obbligatorio'; ok = false; }
            if (!this.surname.trim()) { this.fieldErrors.surname  = 'Campo obbligatorio'; ok = false; }
            if (!this.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
                this.fieldErrors.email = 'Inserisci un indirizzo email valido'; ok = false;
            }
            // Password non richiesta in modalità OAuth (generata dal backend)
            if (!this.oauthCompletion && (!this.password || this.password.length < 8)) {
                this.fieldErrors.password = 'La password deve essere di almeno 8 caratteri'; ok = false;
            }
            const cf = this.fiscalCode.trim().toUpperCase();
            if (!cf || !this._validateCfFormat(cf)) {
                this.fieldErrors.fiscal_code = 'Codice fiscale non valido'; ok = false;
            }
            // Data di nascita: obbligatoria, formato YYYY-MM-DD plausibile
            if (!this.dateOfBirth || !/^\d{4}-\d{2}-\d{2}$/.test(this.dateOfBirth)) {
                this.fieldErrors.date_of_birth = 'Inserisci la data di nascita'; ok = false;
            }
            // Città di nascita: obbligatoria E selezionata dall'elenco Google Places.
            // Solo la selezione popola _birthGLocation, che è ciò che fa salvare
            // citta/provincia/stato di nascita all'API. Il solo testo digitato non basta:
            // verrebbe validato ma poi NON salvato (g_location_of_birth assente).
            if (!this.cityOfBirth.trim() || !this._birthGLocation) {
                this.fieldErrors.city_of_birth = 'Seleziona la città di nascita dall\'elenco'; ok = false;
            }
            return ok;
        },

        _validateStep2() {
            let ok = true;
            if (!this.occupationId)   { this.fieldErrors.occupation  = 'Seleziona una professione'; ok = false; }
            if (!this.disciplineId)   { this.fieldErrors.discipline   = 'Seleziona una disciplina'; ok = false; }
            if (this.orderNumRequired && !this.orderName.trim()) {
                this.fieldErrors.order_name = 'Inserisci il nome dell\'ordine'; ok = false;
            }
            if (this.orderNumRequired && !this.orderNum.trim()) {
                this.fieldErrors.order_num = 'Inserisci il numero di iscrizione all\'ordine'; ok = false;
            }
            if (!this.workSituationId) { this.fieldErrors.work_situation = 'Seleziona una situazione lavorativa'; ok = false; }
            return ok;
        },

        // ── API: professioni e discipline ────────────────────────────
        async _loadOccupations() {
            if (this.occupations.length) return;
            this.loadingOccupations = true;
            try {
                const res = await fetch(`${window.IKOS.apiBase}/v1/occupations`);
                const data = await res.json();
                this.occupations = data._embedded?.items ?? [];
            } catch {
                // silenzioso: l'utente vedrà la select vuota
            } finally {
                this.loadingOccupations = false;
            }
            await this._prefillProfessionFromCookie();
        },

        // Professione (e disciplina) scelte nel catalogo: un campo in meno da compilare.
        // Non sovrascrive una scelta gia' fatta nel form.
        async _prefillProfessionFromCookie() {
            if (this.occupationId) return;
            const known = readProfession();
            if (!known?.id || !this.occupations.some(o => String(o.id) === String(known.id))) return;
            this.occupationId = String(known.id);
            await this.onOccupationChange();
            if (known.discipline && this.disciplines.some(d => String(d.id) === String(known.discipline))) {
                this.disciplineId = String(known.discipline);
            } else if (this.disciplines.length === 1) {
                this.disciplineId = String(this.disciplines[0].id);
            }
        },

        async _loadWorkSituations() {
            if (this.workSituations.length) return;
            try {
                const res = await fetch(`${window.IKOS.apiBase}/v1/work-situations`);
                const data = await res.json();
                this.workSituations = data._embedded?.items ?? [];
            } catch {
                // silenzioso
            }
        },

        async onOccupationChange() {
            this.disciplineId = '';
            this.disciplines = [];
            this.orderName = '';
            this.orderNum = '';
            this.orderNumRequired = false;
            this.fieldErrors.discipline = '';
            this.fieldErrors.order_name = '';
            this.fieldErrors.order_num = '';

            if (!this.occupationId) return;

            const occ = this.occupations.find(o => String(o.id) === String(this.occupationId));
            this.orderNumRequired = occ?.order_num_required ?? false;

            this.loadingDisciplines = true;
            try {
                const res = await fetch(`${window.IKOS.apiBase}/v1/occupations/${this.occupationId}/disciplines?page_size=-1`);
                const data = await res.json();
                this.disciplines = data._embedded?.items ?? [];
            } catch {
                // silenzioso
            } finally {
                this.loadingDisciplines = false;
            }
        },

        // ── Submit finale ────────────────────────────────────────────
        async submit() {
            this.fieldErrors = {};
            if (!this.acceptPrivacy || !this.acceptTerms) {
                this.$store.auth.error = 'Devi accettare la privacy e i termini d\'uso per procedere.';
                return;
            }

            const cf = this.fiscalCode.trim().toUpperCase();

            if (this.oauthCompletion) {
                // Completamento profilo OAuth: aggiorna i dati mancanti via PATCH
                await this._submitOauthCompletion(cf);
            } else {
                // Registrazione classica
                await this._submitRegistration(cf);
            }
        },

        async _submitRegistration(cf) {
            const payload = {
                name:                   this.name.trim(),
                surname:                this.surname.trim(),
                email:                  this.email.trim(),
                password:               this.password,
                password_confirmation:  this.password,
                fiscal_code:            cf,
                date_of_birth:          this.dateOfBirth,
                disciplines: [{
                    occupation_id:  parseInt(this.occupationId),
                    discipline_id:  parseInt(this.disciplineId),
                    order:          this.orderName.trim(),
                    order_num:      this.orderNum.trim(),
                }],
                work_condition:         this.workSituationId,
                accept_privacy_terms:   true,
                accept_use_conditions:  true,
                subscribe_newsletter:   !this.newsletterOptOut,
            };

            // Città di nascita: l'API ricava citta/provincia/stato dai components Google Places
            if (this._birthGLocation) payload.g_location_of_birth = this._birthGLocation;

            // Codice amico da /promo/{code}
            const referralCode = sessionStorage.getItem('ik_referral_code');
            if (referralCode) payload.referral = referralCode;

            const result = await this.$store.auth.register(payload);

            if (!result.success && result.validation_messages) {
                this.fieldErrors = this._flattenValidationMessages(result.validation_messages);
                if (this.fieldErrors.name || this.fieldErrors.surname ||
                    this.fieldErrors.email || this.fieldErrors.password || this.fieldErrors.fiscal_code ||
                    this.fieldErrors.date_of_birth || this.fieldErrors.city_of_birth) {
                    this.step = 1;
                } else if (this.fieldErrors.occupation || this.fieldErrors.discipline ||
                           this.fieldErrors.order_name || this.fieldErrors.order_num || this.fieldErrors.work_situation) {
                    this.step = 2;
                }
            }
        },

        async _submitOauthCompletion(cf) {
            // Utente nuovo OAuth: completa la registrazione via POST /v1/register
            // con una password casuale e il provider_id Google per salvare il link.
            const rndPwd = this._randomPassword();
            const payload = {
                name:                   this.name.trim(),
                surname:                this.surname.trim(),
                email:                  this.email.trim(),
                password:               rndPwd,
                password_confirmation:  rndPwd,
                fiscal_code:            cf,
                date_of_birth:          this.dateOfBirth,
                disciplines: [{
                    occupation_id:  parseInt(this.occupationId),
                    discipline_id:  parseInt(this.disciplineId),
                    order:          this.orderName.trim(),
                    order_num:      this.orderNum.trim(),
                }],
                work_condition:         this.workSituationId,
                accept_privacy_terms:   true,
                accept_use_conditions:  true,
                subscribe_newsletter:   !this.newsletterOptOut,
                google_provider_id:     this.oauthProviderId,
            };

            // Città di nascita: l'API ricava citta/provincia/stato dai components Google Places
            if (this._birthGLocation) payload.g_location_of_birth = this._birthGLocation;

            // Codice amico da /promo/{code}
            const referralCode = sessionStorage.getItem('ik_referral_code');
            if (referralCode) payload.referral = referralCode;

            const result = await this.$store.auth.register(payload);

            if (!result.success && result.validation_messages) {
                this.fieldErrors = this._flattenValidationMessages(result.validation_messages);
                if (this.fieldErrors.name || this.fieldErrors.surname ||
                    this.fieldErrors.email || this.fieldErrors.fiscal_code ||
                    this.fieldErrors.date_of_birth || this.fieldErrors.city_of_birth) {
                    this.step = 1;
                } else if (this.fieldErrors.occupation || this.fieldErrors.discipline ||
                           this.fieldErrors.order_name || this.fieldErrors.order_num || this.fieldErrors.work_situation) {
                    this.step = 2;
                }
            }
        },

        _randomPassword() {
            const arr = new Uint8Array(16);
            crypto.getRandomValues(arr);
            return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
        },

        // ── Traduzione messaggi di errore API ────────────────────────
        _translateApiError(key, message) {
            const translations = {
                // Email
                'emailExist':                   'Questa email è già associata a un account esistente.',
                // Codice fiscale
                'fiscCodeExist':                'Questo codice fiscale è già registrato.',
                'invalidFiscCode':              'Formato codice fiscale non valido.',
                // Cellulare
                'mobilePhoneExist':             'Questo numero di telefono è già in uso.',
                // Disciplina
                'invalidFormatDisciplines':     'Formato discipline non valido.',
                'maxCountDisciplines':          'Puoi inserire al massimo 3 discipline.',
                'disciplineError':              'Disciplina non valida.',
                // Situazione lavorativa
                'invalidWorkCondition':         'Situazione lavorativa non valida.',
                // Referral
                'notValid':                     'Codice referral non valido.',
                // Generico
                'emptyError':                   'I dati inviati sono incompleti.',
            };
            return translations[key] ?? message ?? 'Valore non valido';
        },

        _flattenValidationMessages(messages) {
            const flat = {};
            const fieldMap = {
                email:          'email',
                fiscal_code:    'fiscal_code',
                mobile_phone:   'mobile_phone',
                disciplines:    'discipline',
            };
            for (const [field, errors] of Object.entries(messages)) {
                const flatKey = fieldMap[field] ?? field;
                // Prende il primo errore e lo traduce
                const [errorKey, errorMsg] = Object.entries(errors)[0] ?? ['', ''];
                flat[flatKey] = this._translateApiError(errorKey, errorMsg);
            }
            return flat;
        },
    }));
}
